import { useMemo } from 'react';
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { seatMapFor } from '../../../lib/seatingCandidates';
import TableCanvas from './TableCanvas';
import type { SeatAssignment, SeatingTable } from '../../../types/seating';
import { plural } from '../../../lib/plural';

/**
 * One table in the room: what it is called, how full it is, and the plan itself.
 *
 * The card is deliberately thin — the drawing is TableCanvas, shared with the
 * add-a-table preview so the shape a host picks from a menu is drawn by the same
 * code that draws it for real.
 */
export default function TablePlan({
  table,
  seatPitchCm,
  placing,
  onSeatClick,
  onEdit,
  onDelete,
}: {
  table: SeatingTable;
  seatPitchCm: number;
  placing?: string | null;
  onSeatClick: (seatIndex: number, assignment: SeatAssignment | null) => void;
  /** Resize, reshape, rename, reorder. Delete used to be the only one of these. */
  onEdit: () => void;
  onDelete: () => void;
}) {
  const assignments = useMemo(() => seatMapFor(table), [table]);
  const filled = assignments.size;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {table.label || 'Untitled table'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filled} of {table.capacity} seated
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip size="small" variant="outlined" label={plural(table.capacity, 'seat')} />
          <IconButton
            size="small"
            aria-label={`Edit ${table.label || 'this table'}`}
            onClick={onEdit}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${table.label || 'this table'}`}
            onClick={onDelete}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <TableCanvas
          table={table}
          seatPitchCm={seatPitchCm}
          assignments={assignments}
          placing={placing}
          onSeatClick={onSeatClick}
          ariaLabel={`${table.label || 'Table'} seating plan`}
        />
      </Box>
    </Paper>
  );
}
